/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/ban-types */

import { IHttpProvider, IPromise, IQService, isFunction } from "angular";
import uirouter, { RawParams, StateOrName, StateRegistry, StateService, TransitionOptions, TransitionPromise } from "@uirouter/angularjs";
import uiBootstrap from "@gtmhub/ui-bootstrap.module";
import env from "@gtmhub/env/module";
import { TracingService } from "./tracing.service";
import { TracingInterceptor } from "./tracing.interceptor";
import { InBrowserStatsInterceptor } from "./in-browser-stats.interceptor";
import { INBROWSER_TRACING_STATS } from "@gtmhub/build";
import { $qPatch } from "./zone-patches/q-patch";
import { $timeoutPatch } from "./zone-patches/timeout-patch";
import { $intervalPatch } from "./zone-patches/interval-patch";
import { IModalService, IModalServiceInstance, IModalSettings } from "angular-ui-bootstrap";
import { ITraceRootScopeService } from "./models";
import { Ng1Zone, Ng1ZoneSpec } from "./zone";

const mod = angular.module("tracing", [uirouter, uiBootstrap, env]);
mod.service("TracingService", TracingService);

$qPatch(mod);
$timeoutPatch(mod);
$intervalPatch(mod);

mod.config([
  "$httpProvider",
  ($httpProvider: IHttpProvider) => {
    $httpProvider.interceptors.push(TracingInterceptor.factory());

    if (INBROWSER_TRACING_STATS) {
      $httpProvider.interceptors.push(InBrowserStatsInterceptor.factory());
    }
  },
]);

mod.run([
  "TracingService",
  (tracingService: TracingService) => {
    tracingService.initSessionID();
  },
]);

// Patch $state / $uibModal to support attaching tracing information
const getFileNameFromTemplateUrl = (templateUrl: string): string => templateUrl.replace(".html", "").split("/").pop().split(".")[0];

mod.run([
  "$rootScope",
  "$state",
  "$stateRegistry",
  "$q",
  "$uibModal",
  "TracingService",
  ($rootScope: ITraceRootScopeService, $state: StateService, $stateRegistry: StateRegistry, $q: IQService, $uibModal: IModalService, tracingService: TracingService) => {
    let initZone: Ng1Zone = null;
    let stateZone: Ng1Zone = null;

    const createTracingZoneSpec = (actionName: string): Ng1ZoneSpec => ({
      name: actionName,
      properties: {
        screen: tracingService.getScreen(),
        action: tracingService.createAction(actionName),
      },
    });

    $rootScope.traceInit = <TPromise>(fn: () => IPromise<TPromise>): IPromise<TPromise> => {
      const promise = initZone.run<IPromise<TPromise>>(fn, $rootScope);

      const initPromises = stateZone.get<IPromise<unknown>[]>("initPromises");
      initPromises.push(promise);

      return promise;
    };

    $rootScope.traceAction = <TResult>(name: string, fn: Function, parentZone?: Ng1Zone): TResult => {
      if (!parentZone) {
        parentZone = stateZone;
      }

      const zone = parentZone.fork(createTracingZoneSpec(name));
      return zone.run(fn, $rootScope);
    };

    // patch $uibModal so we can trace opening of modals
    const oldOpen = $uibModal.open;
    $uibModal.open = function tracingOpen(options: IModalSettings): IModalServiceInstance {
      // use the template to create a name for the zone
      if (options.templateUrl) {
        const optionsTemplateUrl: unknown = options.templateUrl;
        const templateUrl: string = isFunction(optionsTemplateUrl) ? optionsTemplateUrl() : optionsTemplateUrl;
        if (templateUrl) {
          const zoneName = getFileNameFromTemplateUrl(templateUrl);
          const parentZone = stateZone || Ng1Zone.root;
          const zone = parentZone.fork(createTracingZoneSpec(zoneName));
          return zone.run<IModalServiceInstance>(oldOpen, this, [...arguments]);
        }
      }
      return oldOpen.apply(this, arguments);
    };

    // patch $state so we can trace state transitions
    const oldTransitionTo = $state.transitionTo;
    $state.transitionTo = function tracingTransitionTo(to: StateOrName, toParams?: RawParams, options?: TransitionOptions): TransitionPromise {
      const base = (options || {}).relative;
      const state = $stateRegistry.get(to, base);
      const stateName = state ? state.name : "<unknown>";

      tracingService.initScreen(stateName);

      stateZone = Ng1Zone.root.fork({
        name: stateName,
        properties: {
          // here we will collect additional init promises from child controllers / directives
          initPromises: [],
        },
      });

      initZone = stateZone.fork(createTracingZoneSpec("init"));

      return initZone.run<TransitionPromise>(oldTransitionTo, this, [...arguments]);
    };
  },
]);

export default mod.name;
